import { useCallback, useEffect, useRef, useState } from 'react'
import { X, Plus, Paperclip, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { MultiSelect } from '@/components/ui/multi-select'
import { MarkdownField, MarkdownPreview } from './MarkdownField'
import { BoardLabelChips } from './BoardLabelChips'
import { MediaPickerSheet } from '@/features/admin/components/MediaPickerSheet'
import { projectBoardsService } from '../services/projectBoards.service'
import { projectBoardCommentsService } from '../services/projectBoardComments.service'
import type {
  ProjectBoardDocument,
  ProjectBoardColumnId,
  ProjectTaskDocument,
  ProjectTaskCommentDocument,
} from '@/types'
import type { MediaDocument } from '@/features/admin/services/admin.media.service'

type Priority = 'low' | 'medium' | 'high'

type TaskAttachment = NonNullable<ProjectTaskDocument['attachments']>[number]

type EditableFields = Partial<
  Pick<
    ProjectTaskDocument,
    | 'title'
    | 'description'
    | 'columnId'
    | 'order'
    | 'assigneeIds'
    | 'dueDate'
    | 'coverMediaId'
    | 'coverMediaUrl'
    | 'attachments'
    | 'priority'
    | 'labelIds'
  >
>

type Props = {
  open: boolean
  mode: 'create' | 'edit'

  boardId: string
  board: ProjectBoardDocument & { id: string }
  members: Array<{ id: string; name: string }>
  currentUser: { uid: string; displayName?: string | null }

  initialColumnId?: ProjectBoardColumnId
  initialOrder?: number

  task?: ProjectTaskDocument & { id: string }

  onClose: () => void
  onSaved: () => void | Promise<void>
  onDrillIn?: (childBoardId: string) => void
}

const PRIORITY_OPTIONS: Array<{ value: Priority | 'none'; label: string; cls: string }> = [
  { value: 'none', label: 'None', cls: 'bg-muted text-muted-foreground' },
  { value: 'low', label: 'Low', cls: 'bg-blue-500 text-white' },
  { value: 'medium', label: 'Med', cls: 'bg-amber-500 text-white' },
  { value: 'high', label: 'High', cls: 'bg-red-500 text-white' },
]

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return (parts[0]?.[0] ?? '?').toUpperCase()
  return ((parts[0]?.[0] ?? '') + (parts[parts.length - 1]?.[0] ?? '')).toUpperCase()
}

function colorForId(id: string): string {
  const palette = [
    'bg-rose-500',
    'bg-orange-500',
    'bg-amber-500',
    'bg-emerald-500',
    'bg-cyan-500',
    'bg-blue-500',
    'bg-violet-500',
    'bg-pink-500',
  ]
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0
  return palette[h % palette.length] ?? 'bg-slate-500'
}

function relativeTime(date: Date | null): string {
  if (!date) return ''
  const diff = Date.now() - date.getTime()
  const sec = Math.round(diff / 1000)
  if (sec < 60) return `${sec}s ago`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min}m ago`
  const hr = Math.round(min / 60)
  if (hr < 24) return `${hr}h ago`
  const days = Math.round(hr / 24)
  if (days < 30) return `${days}d ago`
  return date.toLocaleDateString()
}

function toDate(v: unknown): Date | null {
  if (!v) return null
  if (v instanceof Date) return v
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    try {
      return (v as { toDate: () => Date }).toDate()
    } catch {
      return null
    }
  }
  return null
}

export function TaskDialog({
  open,
  mode,
  boardId,
  board,
  members,
  currentUser,
  initialColumnId,
  initialOrder,
  task,
  onClose,
  onSaved,
  onDrillIn,
}: Props) {
  const firstColumnId = (board.columns[0]?.id ?? 'backlog') as ProjectBoardColumnId

  const [draft, setDraft] = useState<EditableFields>(() =>
    mode === 'edit' && task
      ? { ...task }
      : { columnId: initialColumnId ?? firstColumnId, title: '' }
  )

  useEffect(() => {
    if (mode === 'edit' && task) {
      setDraft({ ...task })
    } else if (mode === 'create') {
      setDraft({ columnId: initialColumnId ?? firstColumnId, title: '' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, mode, open])

  // Comments (edit mode only)
  const [comments, setComments] = useState<Array<ProjectTaskCommentDocument & { id: string }>>([])
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)

  const reloadComments = useCallback(async () => {
    if (mode !== 'edit' || !task) return
    const list = await projectBoardCommentsService.list(boardId, task.id)
    setComments(list)
  }, [mode, task, boardId])

  useEffect(() => {
    if (mode === 'edit' && task && open) {
      void reloadComments()
    } else {
      setComments([])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, open, mode])

  // Media picker state
  const [coverPickerOpen, setCoverPickerOpen] = useState(false)
  const [attachPickerOpen, setAttachPickerOpen] = useState(false)

  // Submitting / creating
  const [creating, setCreating] = useState(false)
  const [creatingChild, setCreatingChild] = useState(false)

  // Description debounce
  const descTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const commitField = useCallback(
    async (patch: EditableFields) => {
      setDraft((prev) => ({ ...prev, ...patch }))
      if (mode === 'edit' && task) {
        await projectBoardsService.updateTask(boardId, task.id, patch)
        await onSaved()
      }
    },
    [mode, task, boardId, onSaved]
  )

  // Local-only update (no remote save) — used while typing
  const updateLocal = useCallback((patch: EditableFields) => {
    setDraft((prev) => ({ ...prev, ...patch }))
  }, [])

  const handleDescriptionChange = (value: string) => {
    updateLocal({ description: value })
    if (mode === 'edit' && task) {
      if (descTimerRef.current) clearTimeout(descTimerRef.current)
      descTimerRef.current = setTimeout(() => {
        void commitField({ description: value })
      }, 500)
    }
  }

  useEffect(() => {
    return () => {
      if (descTimerRef.current) clearTimeout(descTimerRef.current)
    }
  }, [])

  const handleTitleBlur = async () => {
    if (mode !== 'edit' || !task) return
    const current = draft.title ?? ''
    if (current !== (task.title ?? '')) {
      await commitField({ title: current })
    }
  }

  const handleCoverSelect = async (item: MediaDocument) => {
    await commitField({ coverMediaId: item.id, coverMediaUrl: item.url })
  }

  const handleCoverRemove = async () => {
    await commitField({ coverMediaId: '', coverMediaUrl: '' })
  }

  const handleAttachmentAdd = async (item: MediaDocument) => {
    const next: TaskAttachment[] = [
      ...(draft.attachments ?? []),
      { mediaId: item.id, url: item.url, name: item.name, mimeType: item.mimeType },
    ]
    await commitField({ attachments: next })
  }

  const handleAttachmentRemove = async (mediaId: string) => {
    const next = (draft.attachments ?? []).filter((a) => a.mediaId !== mediaId)
    await commitField({ attachments: next })
  }

  const handlePriorityClick = async (value: Priority | 'none') => {
    if (value === 'none') {
      await commitField({ priority: undefined })
    } else {
      await commitField({ priority: value })
    }
  }

  const handleColumnChange = async (value: string) => {
    await commitField({ columnId: value as ProjectBoardColumnId })
  }

  const handleDueDateChange = async (value: string) => {
    await commitField({ dueDate: value })
  }

  const handleCreate = async () => {
    if (!draft.title?.trim()) return
    setCreating(true)
    try {
      const columnId = (draft.columnId ?? initialColumnId ?? firstColumnId) as ProjectBoardColumnId
      const order = initialOrder ?? Date.now()
      await projectBoardsService.createTask(boardId, {
        title: draft.title.trim(),
        columnId,
        order,
        createdBy: currentUser.uid,
        ...(draft.description ? { description: draft.description } : {}),
        ...(draft.assigneeIds && draft.assigneeIds.length > 0
          ? { assigneeIds: draft.assigneeIds }
          : {}),
        ...(draft.dueDate ? { dueDate: draft.dueDate } : {}),
        ...(draft.coverMediaId ? { coverMediaId: draft.coverMediaId } : {}),
        ...(draft.coverMediaUrl ? { coverMediaUrl: draft.coverMediaUrl } : {}),
        ...(draft.attachments && draft.attachments.length > 0
          ? { attachments: draft.attachments }
          : {}),
        ...(draft.priority ? { priority: draft.priority } : {}),
        ...(draft.labelIds && draft.labelIds.length > 0 ? { labelIds: draft.labelIds } : {}),
      })
      await onSaved()
      onClose()
    } finally {
      setCreating(false)
    }
  }

  const handleSubmitComment = async () => {
    if (mode !== 'edit' || !task) return
    const body = newComment.trim()
    if (!body) return
    setSubmittingComment(true)
    try {
      await projectBoardCommentsService.add(boardId, task.id, {
        bodyMarkdown: body,
        authorId: currentUser.uid,
        authorName: currentUser.displayName ?? 'User',
      })
      setNewComment('')
      await reloadComments()
    } finally {
      setSubmittingComment(false)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (mode !== 'edit' || !task) return
    if (!window.confirm('Delete this comment?')) return
    await projectBoardCommentsService.remove(boardId, task.id, commentId)
    await reloadComments()
  }

  const handleCreateChild = async () => {
    if (mode !== 'edit' || !task) return
    setCreatingChild(true)
    try {
      const newBoardId = await projectBoardsService.createChildBoard(boardId, task.id)
      await onSaved()
      onDrillIn?.(newBoardId)
    } finally {
      setCreatingChild(false)
    }
  }

  const handleOpenChild = () => {
    if (mode !== 'edit' || !task?.childBoardId) return
    onDrillIn?.(task.childBoardId)
  }

  const handleDelete = async () => {
    if (mode !== 'edit' || !task) return
    if (!window.confirm('Delete this task?')) return
    await projectBoardsService.deleteTask(boardId, task.id)
    await onSaved()
    onClose()
  }

  const currentPriority: Priority | 'none' = draft.priority ?? 'none'
  const coverUrl = draft.coverMediaUrl

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="w-[90vw] max-w-[90vw] sm:max-w-[90vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="sr-only">
              {mode === 'create' ? 'Create task' : 'Edit task'}
            </DialogTitle>
            <Input
              value={draft.title ?? ''}
              onChange={(e) => updateLocal({ title: e.target.value })}
              onBlur={handleTitleBlur}
              placeholder="Task title"
              className="text-lg font-semibold border-0 shadow-none focus-visible:ring-1 px-2"
            />
          </DialogHeader>

          <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
            {/* LEFT PANE */}
            <div className="space-y-6 min-w-0">
              {/* Cover image */}
              <div>
                {coverUrl ? (
                  <div className="group relative">
                    <img
                      src={coverUrl}
                      alt="Cover"
                      className="h-32 w-full rounded-md object-cover"
                    />
                    <div className="absolute inset-0 hidden items-center justify-center gap-2 rounded-md bg-black/40 group-hover:flex">
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        onClick={() => setCoverPickerOpen(true)}
                      >
                        Change
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="destructive"
                        onClick={handleCoverRemove}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCoverPickerOpen(true)}
                    className="flex h-24 w-full items-center justify-center rounded-md border-2 border-dashed text-sm text-muted-foreground hover:bg-muted/30"
                  >
                    + Add cover
                  </button>
                )}
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label>Description</Label>
                <MarkdownField
                  value={draft.description ?? ''}
                  onChange={handleDescriptionChange}
                  placeholder="Add a description… (markdown supported)"
                  height={220}
                  overflow={false}
                />
              </div>

              {/* Attachments */}
              <div className="space-y-2">
                <Label>Attachments</Label>
                <div className="space-y-1">
                  {(draft.attachments ?? []).length === 0 && (
                    <div className="text-xs text-muted-foreground">No attachments yet.</div>
                  )}
                  {(draft.attachments ?? []).map((a) => {
                    const isImage = a.mimeType?.startsWith('image/')
                    return (
                      <div
                        key={a.mediaId}
                        className="flex items-center gap-2 rounded border bg-muted/30 p-2"
                      >
                        {isImage ? (
                          <img
                            src={a.url}
                            alt={a.name}
                            className="h-8 w-8 rounded object-cover"
                          />
                        ) : (
                          <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                        )}
                        <a
                          href={a.url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 truncate text-sm hover:underline"
                        >
                          {a.name}
                        </a>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleAttachmentRemove(a.mediaId)}
                        >
                          <X className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )
                  })}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setAttachPickerOpen(true)}
                >
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  Add attachment
                </Button>
              </div>

              {/* Activity (edit mode only) */}
              {mode === 'edit' && task && (
                <div className="space-y-3">
                  <Label>Activity</Label>
                  <div className="space-y-3">
                    {comments.length === 0 && (
                      <div className="text-xs text-muted-foreground">No comments yet.</div>
                    )}
                    {comments.map((c) => {
                      const created = toDate((c as unknown as { createdAt?: unknown }).createdAt)
                      return (
                        <div key={c.id} className="rounded border bg-muted/20 p-2">
                          <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                            <span>
                              <span className="font-medium text-foreground">{c.authorName}</span>{' '}
                              · {relativeTime(created)}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              onClick={() => handleDeleteComment(c.id)}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                          <div className="text-sm">
                            <MarkdownPreview source={c.bodyMarkdown} />
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  <div className="space-y-2">
                    <Textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Write a comment… (markdown supported)"
                      rows={3}
                    />
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleSubmitComment}
                        disabled={submittingComment || !newComment.trim()}
                      >
                        {submittingComment ? 'Posting…' : 'Submit'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* RIGHT PANE */}
            <div className="space-y-5">
              {/* Status */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Status
                </Label>
                <Select
                  value={(draft.columnId ?? firstColumnId) as string}
                  onValueChange={handleColumnChange}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {board.columns.map((col) => (
                      <SelectItem key={col.id} value={col.id as string}>
                        {col.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Due date */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Due date
                </Label>
                <Input
                  type="date"
                  value={draft.dueDate ?? ''}
                  onChange={(e) => handleDueDateChange(e.target.value)}
                />
              </div>

              {/* Priority */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Priority
                </Label>
                <div className="flex gap-1">
                  {PRIORITY_OPTIONS.map((opt) => {
                    const active = currentPriority === opt.value
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => handlePriorityClick(opt.value)}
                        className={`flex-1 rounded px-2 py-1 text-xs font-medium transition ${
                          active ? opt.cls : 'bg-muted/40 text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {opt.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Assignees */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Assignees
                </Label>
                <MultiSelect
                  options={members.map((m) => ({
                    value: m.id,
                    label: m.name,
                    icon: (
                      <span
                        className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold text-white ${colorForId(
                          m.id
                        )}`}
                      >
                        {initialsOf(m.name)}
                      </span>
                    ),
                  }))}
                  values={draft.assigneeIds ?? []}
                  onChange={(next) => void commitField({ assigneeIds: next })}
                  placeholder="+ Add assignee"
                  searchPlaceholder="Search members…"
                  emptyMessage="No members found."
                  renderTrigger={(selected) => (
                    <div className="flex min-h-9 w-full items-center gap-1 rounded-md border px-2 py-1.5 text-left hover:bg-muted/30">
                      {selected.length === 0 ? (
                        <span className="text-xs text-muted-foreground">+ Add assignee</span>
                      ) : (
                        <>
                          {selected.slice(0, 3).map((m) => (
                            <span key={m.value} title={m.label}>
                              {m.icon}
                            </span>
                          ))}
                          {selected.length > 3 && (
                            <span className="text-xs text-muted-foreground">
                              +{selected.length - 3}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  )}
                />
              </div>

              {/* Labels */}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                  Labels
                </Label>
                <BoardLabelChips
                  board={board}
                  selectedIds={draft.labelIds ?? []}
                  onChange={(ids) => void commitField({ labelIds: ids })}
                  onLabelsUpdated={onSaved}
                />
              </div>

              {/* Sub-board (edit only) */}
              {mode === 'edit' && task && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    Sub-board
                  </Label>
                  {task.childBoardId ? (
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full"
                      onClick={handleOpenChild}
                    >
                      Open sub-board
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="secondary"
                      className="w-full"
                      onClick={handleCreateChild}
                      disabled={creatingChild}
                    >
                      {creatingChild ? 'Creating…' : 'Create sub-board'}
                    </Button>
                  )}
                </div>
              )}

              {/* Delete (edit only) */}
              {mode === 'edit' && task && (
                <div className="pt-4">
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="w-full"
                    onClick={handleDelete}
                  >
                    <Trash2 className="mr-1 h-3.5 w-3.5" />
                    Delete task
                  </Button>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            {mode === 'create' ? (
              <>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleCreate}
                  disabled={creating || !draft.title?.trim()}
                >
                  {creating ? 'Creating…' : 'Create'}
                </Button>
              </>
            ) : (
              <Button type="button" variant="outline" onClick={onClose}>
                Close
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MediaPickerSheet
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        title="Choose cover image"
        description="Pick an image from the media library."
        withFocusPoint={false}
        mimeTypeFilter="image/"
        currentMediaId={draft.coverMediaId}
        onAssign={() => {}}
        onAssignItem={(item) => void handleCoverSelect(item)}
      />

      <MediaPickerSheet
        open={attachPickerOpen}
        onClose={() => setAttachPickerOpen(false)}
        title="Add attachment"
        description="Pick any media to attach to this task."
        withFocusPoint={false}
        onAssign={() => {}}
        onAssignItem={(item) => void handleAttachmentAdd(item)}
      />
    </>
  )
}

export default TaskDialog
